import { useState, useCallback, useEffect, useRef } from "react";
import AssetTile from "../AssetTile/AssetTile";
import { GroupedInvestmentCategory } from "../../types";
import { useUpdateInvestmentPreferences } from "hooks/investments";
import type { InvestmentTypePreference } from "hooks/investments/UpdateInvestmentPreferencesHook";
import "./AssetTilesList.scss";

interface AssetTilesListProps {
  categories: Record<string, GroupedInvestmentCategory>;
  title?: string;
  banner?: React.ReactNode;
  isOwn?: boolean;
  isArchived?: boolean;
  disableDragAndDrop?: boolean;
  disableCollapse?: boolean;
  blurRestrictedData?: boolean;
  otherSectionCategories?: Record<string, GroupedInvestmentCategory>;
  pendingOpenUuid?: string | null;
  pendingOpenCurrencyUuid?: string | null;
  onPendingOpenComplete?: () => void;
  onSellAllSuccess?: (uuid: string) => void;
  onEditArchiveSuccess?: (uuid: string, isOwn: boolean) => void;
  hideContextMenu?: boolean;
  showCompanyDetails?: boolean;
}

const AssetTilesList = ({
  categories,
  title,
  banner,
  isOwn = false,
  isArchived = false,
  disableDragAndDrop = false,
  disableCollapse = false,
  blurRestrictedData = false,
  otherSectionCategories,
  pendingOpenUuid,
  pendingOpenCurrencyUuid,
  onPendingOpenComplete,
  onSellAllSuccess,
  onEditArchiveSuccess,
  hideContextMenu = false,
  showCompanyDetails = false,
}: AssetTilesListProps) => {
  const getSortedCategories = useCallback(() => {
    return Object.values(categories).sort((a, b) => a.order - b.order);
  }, [categories]);

  const [sortedCategories, setSortedCategories] =
    useState<GroupedInvestmentCategory[]>(getSortedCategories);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const isDragEnabled = !disableDragAndDrop && sortedCategories.length >= 2;
  const isCollapseEnabled = !disableCollapse;

  const { mutate: updatePreferences } = useUpdateInvestmentPreferences();

  const touchDragIndex = useRef<number | null>(null);
  const dragItemSlug = useRef<string | null>(null);
  const isToggleAnimating = useRef(false);
  const toggleAnimationTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleTouchStart = (index: number) => {
    touchDragIndex.current = index;
    dragItemSlug.current = sortedCategories[index]?.investmentType.slug ?? null;
    setDragIndex(index);
    document.body.classList.add("is-dragging");
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const tileEl = el?.closest("[data-tile-index]");
    if (!tileEl) return;
    const overIndex = Number(tileEl.getAttribute("data-tile-index"));
    if (isNaN(overIndex) || overIndex === dragIndex) return;

    // Midpoint check for touch – same logic as mouse drag
    const rect = tileEl.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    if (
      dragIndex !== null &&
      dragIndex < overIndex &&
      touch.clientY <= midpoint
    )
      return;
    if (
      dragIndex !== null &&
      dragIndex > overIndex &&
      touch.clientY >= midpoint
    )
      return;

    handleDragEnter(null, overIndex);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
    touchDragIndex.current = null;
  };

  useEffect(() => {
    const incoming = getSortedCategories();
    setSortedCategories((prev) => {
      // Preserve local isExpanded values so a mid-animation server refetch
      // doesn't change the CSS class and interrupt the CSS grid transition.
      const localExpanded = new Map(
        prev.map((c) => [c.investmentType.slug, c.isExpanded]),
      );
      return incoming.map((cat) => ({
        ...cat,
        isExpanded: localExpanded.has(cat.investmentType.slug)
          ? localExpanded.get(cat.investmentType.slug)!
          : cat.isExpanded,
      }));
    });
  }, [getSortedCategories]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    document.body.classList.add("is-dragging");
    const rect = e.currentTarget.getBoundingClientRect();
    e.dataTransfer.setDragImage(
      e.currentTarget,
      e.clientX - rect.left,
      e.clientY - rect.top,
    );
    dragItemSlug.current = sortedCategories[index]?.investmentType.slug ?? null;
    setDragIndex(index);
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement> | null,
    index: number,
  ) => {
    if (!dragItemSlug.current) return;

    // Midpoint check: swap only when cursor crosses the halfway point of the
    // target element. This prevents back-and-forth flickering between adjacent
    // positions when dragging.
    if (e && dragIndex !== null && dragIndex !== index) {
      const rect = e.currentTarget.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      if (dragIndex < index && e.clientY <= midpoint) return; // moving down – not past midpoint yet
      if (dragIndex > index && e.clientY >= midpoint) return; // moving up – not past midpoint yet
    }

    setSortedCategories((prev) => {
      const currentIndex = prev.findIndex(
        (cat) => cat.investmentType.slug === dragItemSlug.current,
      );
      if (currentIndex === -1 || currentIndex === index) return prev;
      const next = [...prev];
      const [draggedItem] = next.splice(currentIndex, 1);
      next.splice(index, 0, draggedItem);
      return next;
    });
    setDragIndex(index);
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    dragItemSlug.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
    setTimeout(() => document.body.classList.remove("is-dragging"), 500);

    const reorderedCategories = sortedCategories.map((cat, index) => ({
      ...cat,
      order: index,
    }));

    setSortedCategories(reorderedCategories);

    const section: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "own_archived"
        : "reliance_archived"
      : isOwn
      ? "own"
      : "reliance";
    const preferences = reorderedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      section,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    const otherSection: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "reliance_archived"
        : "own_archived"
      : isOwn
      ? "reliance"
      : "own";
    const otherPreferences = Object.values(otherSectionCategories ?? {}).map(
      (cat) => ({
        slug: cat.investmentType.slug,
        section: otherSection,
        order: cat.order,
        isExpanded: cat.isExpanded,
      }),
    );

    updatePreferences({ preferences: [...preferences, ...otherPreferences] });

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleToggleExpand = (slug: string, isExpanded: boolean) => {
    if (isToggleAnimating.current) return;

    isToggleAnimating.current = true;
    if (toggleAnimationTimeout.current !== null) {
      clearTimeout(toggleAnimationTimeout.current);
    }
    toggleAnimationTimeout.current = setTimeout(() => {
      isToggleAnimating.current = false;
    }, 300);

    const updatedCategories = sortedCategories.map((cat) =>
      cat.investmentType.slug === slug ? { ...cat, isExpanded } : cat,
    );

    setSortedCategories(updatedCategories);

    const section: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "own_archived"
        : "reliance_archived"
      : isOwn
      ? "own"
      : "reliance";
    const preferences = updatedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      section,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    const otherSection: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "reliance_archived"
        : "own_archived"
      : isOwn
      ? "reliance"
      : "own";
    const otherPreferences = Object.values(otherSectionCategories ?? {}).map(
      (cat) => ({
        slug: cat.investmentType.slug,
        section: otherSection,
        order: cat.order,
        isExpanded: cat.isExpanded,
      }),
    );

    // Delay API call until after the 300 ms CSS animation so the
    // query invalidation / refetch doesn't happen mid-transition.
    setTimeout(() => {
      updatePreferences({ preferences: [...preferences, ...otherPreferences] });
    }, 350);
  };

  if (sortedCategories.length === 0) {
    return null;
  }

  return (
    <div className="asset-tiles-list">
      {title && <h3 className="asset-tiles-list__title">{title}</h3>}
      {banner && <div className="asset-tiles-list__banner">{banner}</div>}
      <div className="asset-tiles-list__items">
        {sortedCategories.map((category, index) => (
          <AssetTile
            key={category.investmentType.slug}
            category={category}
            index={index}
            isOwn={isOwn}
            isArchived={isArchived}
            onToggleExpand={handleToggleExpand}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            isDragging={dragIndex === index}
            isDragOver={dragOverIndex === index}
            isDragEnabled={isDragEnabled}
            isCollapseEnabled={isCollapseEnabled}
            blurRestrictedData={blurRestrictedData}
            pendingOpenUuid={pendingOpenUuid}
            pendingOpenCurrencyUuid={pendingOpenCurrencyUuid}
            onPendingOpenComplete={onPendingOpenComplete}
            onSellAllSuccess={onSellAllSuccess}
            onEditArchiveSuccess={onEditArchiveSuccess}
            hideContextMenu={hideContextMenu}
            showCompanyDetails={showCompanyDetails}
          />
        ))}
      </div>
    </div>
  );
};

export default AssetTilesList;
